import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Mic, Play, X, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { askLive, liveApi } from "@/lib/simranLive";
import { useSpeechInput } from "@/lib/zoya";

export const AskPanel = ({ lec, section, onResume }) => {
  const { L } = useLang();
  const [q, setQ] = useState("");
  const [asked, setAsked] = useState("");
  const [ans, setAns] = useState("");
  const [busy, setBusy] = useState(false);
  const audio = useRef(typeof Audio !== "undefined" ? new Audio() : null);
  const mic = useSpeechInput(lec.lang, setQ);
  useEffect(() => () => audio.current?.pause(), []);
  const send = async () => {
    const question = q.trim();
    if (question.length < 2 || busy) return;
    setBusy(true); setAsked(question); setAns(""); setQ("");
    try {
      const full = await askLive(lec.id, question, section, setAns);
      const deva = (full.match(/[\u0900-\u097F]/g) || []).length / Math.max(1, (full.match(/\p{L}/gu) || []).length);
      const v = await liveApi.tts(full, deva > 0.3 ? "hi" : "en").catch(() => null);
      if (v) { audio.current.src = `data:audio/mpeg;base64,${v.audio}`; audio.current.play().catch(() => {}); }
    } catch (e) {
      setAns(e.message === "busy" ? L("Many students are asking right now. Try again in a minute.", "अभी बहुत लोग पूछ रहे हैं। एक मिनट बाद फिर पूछिए।") : L("Sorry, I couldn't answer that right now. Please try again.", "माफ़ कीजिए, अभी जवाब नहीं दे पाई। फिर से पूछिए।"));
    } finally { setBusy(false); }
  };
  const resume = () => { audio.current?.pause(); onResume(); };
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl bg-white text-[#0B1F3A] p-5 sm:p-6 shadow-2xl" data-testid="live-ask-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="font-extrabold text-lg">{L("Lecture paused. What's your doubt?", "लेक्चर रुका है। आपका सवाल क्या है?")}</div>
        <button type="button" onClick={resume} aria-label="Close" data-testid="live-ask-close" className="w-9 h-9 rounded-full grid place-items-center hover:bg-slate-100"><X className="w-5 h-5" /></button>
      </div>
      {asked && (
        <div className="mt-4 space-y-2" data-testid="live-ask-thread">
          <div className="ml-auto max-w-[85%] w-fit rounded-2xl rounded-br-md bg-[#0B1F3A] text-white px-4 py-2 text-sm" data-testid="live-ask-question">{asked}</div>
          <div className="max-w-[90%] rounded-2xl rounded-bl-md bg-[#F0FDFA] border border-[#99F6E4] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap" data-testid="live-ask-answer">
            {ans || <Loader2 className="w-4 h-4 animate-spin text-[#0F766E]" />}
          </div>
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} maxLength={300} autoFocus disabled={busy} data-testid="live-ask-input"
          placeholder={L("Type or speak your question…", "अपना सवाल लिखिए या बोलिए…")} className="flex-1 min-w-0 h-12 px-4 rounded-full bg-slate-100 border border-slate-200 focus:border-[#14B8A6] outline-none text-sm" />
        {mic.supported && <button type="button" onClick={mic.start} aria-label="Speak" data-testid="live-ask-mic"
          className={`w-12 h-12 shrink-0 rounded-full grid place-items-center border ${mic.listening ? "bg-[#FF3B3B] border-[#FF3B3B] text-white animate-pulse" : "border-slate-300 hover:bg-slate-100"}`}><Mic className="w-5 h-5" /></button>}
        <button type="submit" disabled={busy || q.trim().length < 2} aria-label="Ask" data-testid="live-ask-send"
          className="w-12 h-12 shrink-0 rounded-full grid place-items-center bg-[#F59E0B] text-[#0B1F3A] hover:bg-[#FBBF24] disabled:opacity-40"><Send className="w-5 h-5" /></button>
      </form>
      <button type="button" onClick={resume} disabled={busy} data-testid="live-ask-resume"
        className="mt-4 h-11 px-5 rounded-full bg-[#14B8A6] text-[#0B1F3A] font-black inline-flex items-center gap-2 hover:bg-[#0F766E] hover:text-white transition-colors disabled:opacity-40"><Play className="w-4 h-4" />{L("Resume lecture", "लेक्चर फिर शुरू करें")}</button>
    </motion.div>
  );
};
